/**
 * Client wrappers for the parent <-> child connection Cloud Functions.
 *
 * Flow:
 *   - The child calls createConnectionCode() and shares the returned code
 *     (shown as text + QR) with a parent.
 *   - The child polls getConnectionStatus() until status === "linked".
 *   - The parent calls linkParentToChild() with the scanned/typed code.
 *
 * See serverless/functions/src/index.ts for the matching server logic and
 * utils/parentStudentFirestore.ts for reading the resulting links.
 */
import { callCloudFunction, FIREBASE_FUNCTIONS } from './firebaseFunctions';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export interface CreateCodeResult {
  success: boolean;
  code?: string;
  expiresAt?: string;
  error?: string;
}

export interface ConnectionStatusResult {
  success: boolean;
  status?: 'pending' | 'linked';
  childName?: string;
  childEmail?: string;
  parentEmail?: string | null;
  parentName?: string | null;
  expired?: boolean;
  error?: string;
}

export interface LinkResult {
  success: boolean;
  childName?: string;
  childEmail?: string;
  error?: string;
}

export interface UnlinkResult {
  success: boolean;
  error?: string;
}

/** The child requests (or re-uses) a connection code to share with a parent. */
export async function createConnectionCode(params: {
  childEmail: string;
  childName: string;
  userId?: string | null;
}): Promise<CreateCodeResult> {
  try {
    const res = await callCloudFunction(
      'createConnectionCode',
      FIREBASE_FUNCTIONS.createConnectionCode(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          childEmail: params.childEmail,
          childName: params.childName,
          userId: params.userId ?? null,
        }),
      },
    );
    return (await res.json()) as CreateCodeResult;
  } catch (err) {
    console.warn('createConnectionCode failed:', err);
    return { success: false, error: 'network' };
  }
}

/** The child polls the status of a code to detect when a parent has linked. */
export async function getConnectionStatus(code: string): Promise<ConnectionStatusResult> {
  try {
    const res = await callCloudFunction(
      'getConnectionStatus',
      FIREBASE_FUNCTIONS.getConnectionStatus(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ code }),
      },
    );
    return (await res.json()) as ConnectionStatusResult;
  } catch (err) {
    console.warn('getConnectionStatus failed:', err);
    return { success: false, error: 'network' };
  }
}

/** The parent submits a scanned/typed code to link to a child. */
export async function linkParentToChild(params: {
  code: string;
  parentEmail: string;
  parentName?: string;
}): Promise<LinkResult> {
  try {
    const res = await callCloudFunction(
      'linkParentToChild',
      FIREBASE_FUNCTIONS.linkParentToChild(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          code: params.code,
          parentEmail: params.parentEmail,
          parentName: params.parentName ?? '',
        }),
      },
    );
    return (await res.json()) as LinkResult;
  } catch (err) {
    console.warn('linkParentToChild failed:', err);
    return { success: false, error: 'Server error. Please try again.' };
  }
}

/** Removes a parent <-> child connection. Either side may call this. */
export async function unlinkConnection(params: {
  parentEmail: string;
  childEmail: string;
}): Promise<UnlinkResult> {
  try {
    const res = await callCloudFunction(
      'unlinkParentChild',
      FIREBASE_FUNCTIONS.unlinkParentChild(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          parentEmail: params.parentEmail,
          childEmail: params.childEmail,
        }),
      },
    );
    return (await res.json()) as UnlinkResult;
  } catch (err) {
    console.warn('unlinkConnection failed:', err);
    return { success: false, error: 'network' };
  }
}

/**
 * Normalizes a scanned QR payload down to a bare connection code.
 * Accepts either a raw code ("K7P2QX") or a deep link
 * (".../connect-parent?code=K7P2QX") and returns the uppercased code, or null.
 */
export function extractCodeFromScan(raw: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  // Try to pull a ?code= / &code= param out of a URL.
  const match = trimmed.match(/[?&]code=([A-Za-z0-9]+)/);
  const candidate = match ? match[1] : trimmed;
  const cleaned = candidate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return cleaned.length >= 4 ? cleaned : null;
}

/**
 * Builds the QR image URL for a connection code. The QR encodes a deep link to
 * the parent connect page so an external camera app opens straight into it; the
 * in-app scanner reads the code= param via extractCodeFromScan().
 */
export function connectionQrImageUrl(code: string, size = 220): string {
  let origin = typeof window !== 'undefined' ? window.location.origin : '';
  origin = origin.includes('localhost')
    ? 'https://tinyurl.com/kol-torah-2'
    : 'https://tinyurl.com/koltorah';
  const data = `${origin}/connect-parent?code=${encodeURIComponent(code)}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(
    data,
  )}`;
}
